// VideoThankYouScreen.tsx
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Mail, Clock, Video, ArrowRight } from 'lucide-react';

interface VideoThankYouScreenProps {
  testToken?: string;
  onContinueToQuestionnaire?: () => void;
  isUnifiedFlow?: boolean;
}

const VideoThankYouScreen: React.FC<VideoThankYouScreenProps> = ({ 
  testToken: propTestToken,
  onContinueToQuestionnaire,
  isUnifiedFlow = false
}) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;

  useEffect(() => {
    console.log('Video test completed with token:', testToken);
  }, [testToken]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          {/* Main Heading */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Video Assessment Complete!
          </h1>

          {/* Thank You Message */}
          <p className="text-xl text-gray-700 mb-8">
            Thank you for completing the video assessment.
          </p>

          {/* Information Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-indigo-50 rounded-lg p-6">
              <div className="flex items-center justify-center mb-3">
                <Video className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Videos Recorded</h3>
              <p className="text-sm text-gray-600">
                All 3 responses submitted
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center justify-center mb-3">
                <Mail className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {isUnifiedFlow || window.location.pathname.includes('/unified-test/') ? 'Next: Questionnaire' : "We'll Be In Touch"}
              </h3>
              <p className="text-sm text-gray-600">
                {isUnifiedFlow || window.location.pathname.includes('/unified-test/')
                  ? 'Final assessment section'
                  : 'Our team will review your videos'}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-center justify-center mb-3">
                <Clock className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">What's Next?</h3>
              <p className="text-sm text-gray-600">
                {isUnifiedFlow || window.location.pathname.includes('/unified-test/')
                  ? 'Text-based questions ahead'
                  : 'Review takes 3-5 business days'}
              </p>
            </div>
          </div>

          {/* Detailed Message */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <p className="text-gray-700 leading-relaxed">
              Your video responses have been successfully submitted and our recruitment team has been notified. 
              We appreciate the time you've taken to complete this video assessment.
            </p>
            {(isUnifiedFlow || window.location.pathname.includes('/unified-test/')) ? (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-gray-700 leading-relaxed font-medium mb-2">
                  Final Step: General Questionnaire
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  You're almost done! The final section is a general questionnaire where you'll provide text-based answers to questions. 
                  Some questions may allow you to attach supporting documents (like payslips or certificates) if needed. 
                  Take your time to provide thoughtful and complete responses.
                </p>
              </div>
            ) : (
              <p className="text-gray-700 leading-relaxed mt-4">
                Please keep an eye on your email for updates regarding the next steps in the recruitment process.
              </p>
            )}
          </div>

          {/* Contact Information */}
          <div className="border-t pt-6">
            <p className="text-sm text-gray-500">
              If you have any questions, please don't hesitate to contact us at{' '}
              <a href="https://mail.google.com/mail/?view=cm&to=hr@telerelation.com" className="text-brand-500 hover:text-brand-600 font-medium">
                hr@telerelation.com
              </a>
            </p>
          </div>

          {/* Continue Button */}
          <button
            onClick={onContinueToQuestionnaire || (() => window.location.href = "https://telerelation.com/")}
            className="mt-6 px-8 py-3 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 mx-auto"
          >
            {isUnifiedFlow || window.location.pathname.includes('/unified-test/') ? (
              <>
                Continue to Questionnaire
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Go to Website
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoThankYouScreen;
